// Every contact form on the site submits through this module.
//
// Three things have to happen when somebody sends an enquiry: it gets stored,
// somebody gets told about it, and the conversion gets recorded. Only the
// first of those used to happen — enquiries landed in a spreadsheet nobody was
// alerted about, and nothing was reported to Analytics. Both are handled here
// so that a change reaches all 50+ forms at once instead of being copied into
// each page.
//
// Storage is the only step that can fail the submission. Notification and
// tracking are best-effort: a lead that was saved but not emailed is still a
// lead, and we must never tell someone their message failed when it did not.

const SHEET_ENDPOINT = "https://sheetdb.io/api/v1/8d909teoly1pe";

// EmailJS — the same service and public key the careers form already uses.
// The template is a separate one because the careers template is built around
// a CV attachment and none of its variables match an enquiry.
const EMAILJS_SERVICE_ID = "service_kdqpdc7";
const EMAILJS_PUBLIC_KEY = "9mH7ATAru_udAbuol";

// SET THIS to enable enquiry notification emails.
//
// Create a template in the EmailJS dashboard whose body uses these variables:
//   {{fullname}} {{email}} {{phone}} {{subject}} {{description}}
//   {{page_url}} {{source}} {{submitted_at}}
// and set its "To email" to whoever should answer enquiries. Then paste the
// template ID here. Until it is set, enquiries are still saved to the sheet —
// they just arrive silently, which is the situation this constant exists to
// end.
const EMAILJS_CONTACT_TEMPLATE_ID = "";

let warnedAboutTemplate = false;

// Fire-and-forget. Loaded on demand so emailjs-com stays out of the initial
// bundle — it is only needed at the moment somebody actually submits.
function notify(params) {
    if (!EMAILJS_CONTACT_TEMPLATE_ID) {
        if (import.meta.env.DEV && !warnedAboutTemplate) {
            warnedAboutTemplate = true;
            console.warn(
                "[leads] Enquiry saved, but no notification sent: set " +
                "EMAILJS_CONTACT_TEMPLATE_ID in src/controller/leads.js."
            );
        }
        return;
    }

    import("emailjs-com")
        .then(({ default: emailjs }) =>
            emailjs.send(
                EMAILJS_SERVICE_ID,
                EMAILJS_CONTACT_TEMPLATE_ID,
                params,
                EMAILJS_PUBLIC_KEY
            )
        )
        .catch((error) => {
            // The enquiry is already stored, so this is a monitoring problem
            // rather than a user-facing one. Report it as an event so a
            // silently broken notification path is visible in Analytics.
            track("lead_notify_failed", { error_message: String(error) });
        });
}

// GTM is already on every page; this is the event it was never given.
function track(event, payload) {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...payload });
}

/**
 * Submit an enquiry. Resolves when it has been stored, rejects if it has not,
 * so callers can keep showing their existing success and error states.
 *
 * @param {object} formData  fullname, phone, email, subject, description
 * @param {string} source    which form it came from ("page" or "modal")
 */
export async function submitLead(formData, source = "page") {
    const page = typeof window === "undefined" ? "" : window.location.pathname;

    let response;
    try {
        response = await fetch(SHEET_ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(formData),
        });
    } catch (error) {
        // Network-level failure: the request never landed.
        track("lead_submit_failed", { form_source: source, page_path: page, error_message: String(error) });
        throw error;
    }

    // The previous code called .json() straight away, so a rejected write
    // still ran the success branch — the visitor was thanked for an enquiry
    // that was never saved.
    if (!response.ok) {
        track("lead_submit_failed", { form_source: source, page_path: page, status_code: response.status });
        throw new Error("Enquiry was not saved (HTTP " + response.status + ")");
    }

    track("generate_lead", {
        form_source: source,
        page_path: page,
        enquiry_subject: formData.subject || "",
    });

    notify({
        fullname: formData.fullname || "",
        email: formData.email || "",
        phone: formData.phone || "(not provided)",
        subject: formData.subject || "",
        description: formData.description || "",
        page_url: typeof window === "undefined" ? "" : window.location.href,
        source,
        submitted_at: new Date().toISOString(),
    });

    return response;
}
